// TODO(workplace-requests): remove the mock overlay once the backend supports the
// ticket-type fields listed in docs/workplace-requests-backend-api-gaps.md.
import {
  applyTypeMockOverlay,
  applyTypeMockOverlayList,
  persistTypeMockFromParams
} from "../mocks/workplace-requests-mock";
import {
  CreateWorkplaceRequestTypeParams,
  CreateWorkplaceRequestTypeStatusParams,
  CreateWorkplaceRequestWorkflowParams,
  ReorderWorkplaceRequestTypeStatusesParams,
  UpdateWorkplaceRequestTypeParams,
  UpdateWorkplaceRequestTypeStatusParams,
  UpdateWorkplaceRequestWorkflowParams,
  WorkplaceRequestTypeDeleteResponse,
  WorkplaceRequestTypeElement,
  WorkplaceRequestTypeImageFormat,
  WorkplaceRequestTypeStatus,
  WorkplaceRequestTypesParams,
  WorkplaceRequestTypesResponse,
  WorkplaceRequestTypeUpdateResponse,
  WorkplaceRequestWorkflow
} from "../types/workplace-request-types";
import { WorkplaceRequestUploadResponse } from "../types/workplace-requests";
import { BaseSettingsEndpoints } from "./base";
import { ResponseWithStatus } from "./response/response_with_status";

const WORKPLACE_REQUEST_TYPES_BASE_PATH = "/v1/tickets/types";

type OnUserUpdate = {
  created: string;
  ticket_type_id: string;
  updated: string;
  user_id: string;
};

/**
 * The backend stores a ticket type's icon as an object (`{ identifier }`), while
 * the client exposes it as a plain identifier string. These helpers translate
 * between the two on the response and request boundary.
 */
type WireTicketTypeIcon = { identifier: string };

function iconFromWire(
  type: WorkplaceRequestTypeElement
): WorkplaceRequestTypeElement {
  const rawIcon = (type as { icon?: WireTicketTypeIcon | string | null }).icon;
  const icon =
    typeof rawIcon === "object" && rawIcon !== null
      ? rawIcon.identifier
      : (rawIcon ?? null);
  return { ...type, icon };
}

/**
 * Replace a string `icon` in create/update params with the backend's object
 * shape (`{ identifier } | null`). An empty/absent icon clears it (`null`).
 */
function withWireIcon<T extends { icon?: string | null }>(
  data: T
): Omit<T, "icon"> & { icon?: WireTicketTypeIcon | null } {
  const { icon, ...rest } = data;
  // Omit the icon entirely when the caller didn't set it, so an update never
  // clears an existing icon by sending `null`.
  if (!("icon" in data)) {
    return rest;
  }
  return { ...rest, icon: icon ? { identifier: icon } : null };
}

export class WorkplaceRequestTypesClient extends BaseSettingsEndpoints {
  async getWorkplaceRequestTypes({
    query,
    user_id,
    managed,
    isAdmin = false,
    resource_id,
    office_id
  }: WorkplaceRequestTypesParams = {}) {
    const params = new URLSearchParams();

    if (query) {
      params.set("query", query);
    }

    if (user_id && user_id.length > 0) {
      user_id.forEach((id) => params.append("user_id", id));
    }

    // The API rejects `managed` together with `user_id`, and defaults it to
    // false, so it is only ever sent when explicitly asked for on its own.
    if (managed && !user_id?.length) {
      params.set("managed", "true");
    }

    // The API rejects the two location filters together, and the resource is the more
    // precise of the two — it applies the resource-level location rules, not just the
    // office ones — so it wins whenever both are given.
    if (resource_id) {
      params.set("resource_id", resource_id);
    } else if (office_id) {
      params.set("office_id", office_id);
    }

    const queryString = params.toString();
    // `managed` exists only on the admin list endpoint. `/v1/tickets/me/types`
    // has no such filter and would quietly answer a different question — the
    // types the user may *submit* — so asking for managed types picks the
    // endpoint that can honour it.
    const baseUrl =
      isAdmin || params.has("managed")
        ? WORKPLACE_REQUEST_TYPES_BASE_PATH
        : "/v1/tickets/me/types";
    const url = `${baseUrl}${queryString ? `?${queryString}` : ""}`;

    const res = await this.get<WorkplaceRequestTypesResponse>(url);
    // TODO(workplace-requests): overlay mock-only type fields onto the response.
    if (res.ok && res.result?.elements) {
      return ResponseWithStatus.CreateFromResult<WorkplaceRequestTypesResponse>(
        {
          ...res.result,
          elements: applyTypeMockOverlayList(
            res.result.elements.map(iconFromWire)
          )
        },
        res.status
      );
    }
    return res;
  }

  async createWorkplaceRequestType(data: CreateWorkplaceRequestTypeParams) {
    const body = withWireIcon(data);
    const res = await this.post<typeof body, WorkplaceRequestTypeElement>(
      WORKPLACE_REQUEST_TYPES_BASE_PATH,
      body
    );
    // TODO(workplace-requests): persist + overlay mock-only fields for the new type.
    if (res.ok && res.result?.id) {
      persistTypeMockFromParams(res.result.id, data);
      return ResponseWithStatus.CreateFromResult<WorkplaceRequestTypeElement>(
        applyTypeMockOverlay(iconFromWire(res.result)),
        res.status
      );
    }
    return res;
  }

  async updateWorkplaceRequestType(
    id: string,
    data: UpdateWorkplaceRequestTypeParams
  ) {
    const body = withWireIcon(data);
    const res = await this.update<
      typeof body,
      WorkplaceRequestTypeUpdateResponse
    >(`${WORKPLACE_REQUEST_TYPES_BASE_PATH}/${id}`, body);
    // TODO(workplace-requests): persist + overlay mock-only fields for the updated type.
    persistTypeMockFromParams(id, data);
    if (res.ok && res.result?.id) {
      // The overlay only touches the ticket-type fields; `disabled_workflows`
      // rides alongside them and must survive it.
      return ResponseWithStatus.CreateFromResult<WorkplaceRequestTypeUpdateResponse>(
        {
          ...applyTypeMockOverlay(iconFromWire(res.result)),
          disabled_workflows: res.result.disabled_workflows
        },
        res.status
      );
    }
    return res;
  }

  async deleteWorkplaceRequestType(id: string) {
    return this.delete<WorkplaceRequestTypeDeleteResponse>(
      `${WORKPLACE_REQUEST_TYPES_BASE_PATH}/${id}`
    );
  }

  /**
   * Request a signed URL to upload an image for a `single_select` /
   * `multi_select` field option. Upload the image to `upload_url`, then send the
   * returned `path` as the option's `image_path` on create/update.
   */
  async generateFieldOptionImageUploadUrl(
    format: WorkplaceRequestTypeImageFormat
  ) {
    const url = `${WORKPLACE_REQUEST_TYPES_BASE_PATH}/uploads`;

    return this.post<
      { format: WorkplaceRequestTypeImageFormat },
      WorkplaceRequestUploadResponse
    >(url, { format });
  }

  // TEMPORARY: backwards-compat shim for ticket-type user assignments. Remove once the
  // frontend assigns users to categories instead of ticket types.
  async addWorkplaceRequestTypeUser(typeId: string, userId: string) {
    return this.post<null, OnUserUpdate>(
      `${WORKPLACE_REQUEST_TYPES_BASE_PATH}/${typeId}/users/${userId}`,
      null
    );
  }

  // TEMPORARY: backwards-compat shim for ticket-type user assignments. Remove once the
  // frontend assigns users to categories instead of ticket types.
  async removeWorkplaceRequestTypeUser(typeId: string, userId: string) {
    return this.delete<OnUserUpdate>(
      `${WORKPLACE_REQUEST_TYPES_BASE_PATH}/${typeId}/users/${userId}`
    );
  }

  // Ticket type workflows
  //
  // Workflows run an action when a trigger event fires for a ticket of this
  // type. They are listed embedded in the ticket type response (`workflows[]`)
  // and managed through these CRUD endpoints.

  async createWorkplaceRequestTypeWorkflow(
    typeId: string,
    data: CreateWorkplaceRequestWorkflowParams
  ) {
    return this.post<
      CreateWorkplaceRequestWorkflowParams,
      WorkplaceRequestWorkflow
    >(`${WORKPLACE_REQUEST_TYPES_BASE_PATH}/${typeId}/workflows`, data);
  }

  async updateWorkplaceRequestTypeWorkflow(
    typeId: string,
    workflowId: string,
    data: UpdateWorkplaceRequestWorkflowParams
  ) {
    return this.update<
      UpdateWorkplaceRequestWorkflowParams,
      WorkplaceRequestWorkflow
    >(
      `${WORKPLACE_REQUEST_TYPES_BASE_PATH}/${typeId}/workflows/${workflowId}`,
      data
    );
  }

  async deleteWorkplaceRequestTypeWorkflow(typeId: string, workflowId: string) {
    return this.delete<void>(
      `${WORKPLACE_REQUEST_TYPES_BASE_PATH}/${typeId}/workflows/${workflowId}`
    );
  }

  // Ticket type statuses (sub-statuses)

  async getWorkplaceRequestTypeStatuses(typeId: string) {
    return this.get<WorkplaceRequestTypeStatus[]>(
      `${WORKPLACE_REQUEST_TYPES_BASE_PATH}/${typeId}/statuses`
    );
  }

  async createWorkplaceRequestTypeStatus(
    typeId: string,
    data: CreateWorkplaceRequestTypeStatusParams
  ) {
    return this.post<
      CreateWorkplaceRequestTypeStatusParams,
      WorkplaceRequestTypeStatus
    >(`${WORKPLACE_REQUEST_TYPES_BASE_PATH}/${typeId}/statuses`, data);
  }

  async updateWorkplaceRequestTypeStatus(
    typeId: string,
    statusId: string,
    data: UpdateWorkplaceRequestTypeStatusParams
  ) {
    return this.update<
      UpdateWorkplaceRequestTypeStatusParams,
      WorkplaceRequestTypeStatus
    >(
      `${WORKPLACE_REQUEST_TYPES_BASE_PATH}/${typeId}/statuses/${statusId}`,
      data
    );
  }

  async deleteWorkplaceRequestTypeStatus(typeId: string, statusId: string) {
    return this.delete<WorkplaceRequestTypeStatus>(
      `${WORKPLACE_REQUEST_TYPES_BASE_PATH}/${typeId}/statuses/${statusId}`
    );
  }

  async reorderWorkplaceRequestTypeStatuses(
    typeId: string,
    data: ReorderWorkplaceRequestTypeStatusesParams
  ) {
    return this.update<ReorderWorkplaceRequestTypeStatusesParams, void>(
      `${WORKPLACE_REQUEST_TYPES_BASE_PATH}/${typeId}/statuses/positions`,
      data
    );
  }
}
